import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../supabaseClient'
import { Plus, X, Lock, Globe, FolderKanban, Users, Pencil, Search } from 'lucide-react'

const VISIBILITA = [
  {
    id: 'privata',
    label: 'Privata',
    desc: 'Solo tu puoi vederla',
    icon: Lock,
    bg: 'bg-gray-700/50',
    text: 'text-gray-300',
    border: 'border-gray-600',
    dot: 'bg-gray-400',
  },
  {
    id: 'pubblica',
    label: 'Pubblica',
    desc: 'Visibile a tutti',
    icon: Globe,
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-400',
    border: 'border-emerald-500/30',
    dot: 'bg-emerald-400',
  },
  {
    id: 'progetto',
    label: 'Progetto',
    desc: 'Solo i membri del progetto',
    icon: FolderKanban,
    bg: 'bg-violet-500/10',
    text: 'text-violet-400',
    border: 'border-violet-500/30',
    dot: 'bg-violet-400',
  },
  {
    id: 'condivisa',
    label: 'Condivisa',
    desc: 'Solo le persone selezionate',
    icon: Users,
    bg: 'bg-blue-500/10',
    text: 'text-blue-400',
    border: 'border-blue-500/30',
    dot: 'bg-blue-400',
  },
]

const visCfg = (id) => VISIBILITA.find(v => v.id === id) || VISIBILITA[0]

const FILTRI = ['tutte', 'mie', 'pubblica', 'progetto', 'condivisa']
const FILTRI_LABEL = { tutte: 'Tutte', mie: 'Mie', pubblica: 'Pubbliche', progetto: 'Progetto', condivisa: 'Condivise' }

// ─── NoteModal ──────────────────────────────────────────────────────────────────
const NoteModal = ({ nota, progetti, profiles, onClose, onSaved, onDeleted }) => {
  const { profile } = useAuth()
  const isEdit = !!nota?.id
  const [titolo, setTitolo] = useState(nota?.titolo || '')
  const [contenuto, setContenuto] = useState(nota?.contenuto || '')
  const [visibilita, setVisibilita] = useState(nota?.visibilita || 'privata')
  const [projectId, setProjectId] = useState(nota?.project_id || '')
  const [condivisiCon, setCondivisiCon] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  useEffect(() => {
    if (!isEdit) return
    supabase.from('note_condivisioni').select('user_id').eq('note_id', nota.id)
      .then(({ data }) => setCondivisiCon((data || []).map(d => d.user_id)))
  }, [isEdit, nota?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!contenuto.trim()) return
    setLoading(true); setError(null)
    const payload = {
      titolo: titolo.trim() || null,
      contenuto: contenuto.trim(),
      visibilita,
      project_id: visibilita === 'progetto' ? (projectId || null) : null,
      updated_at: new Date().toISOString(),
    }
    let data, err
    if (isEdit) {
      ;({ data, error: err } = await supabase.from('note').update(payload).eq('id', nota.id)
        .select('*, progetto:project_id(titolo,colore), autore:created_by(nickname,full_name,avatar_url)').single())
    } else {
      ;({ data, error: err } = await supabase.from('note').insert({ ...payload, created_by: profile.id })
        .select('*, progetto:project_id(titolo,colore), autore:created_by(nickname,full_name,avatar_url)').single())
    }
    if (err) { setError(err.message); setLoading(false); return }

    // Gestione condivisioni
    if (visibilita === 'condivisa') {
      await supabase.from('note_condivisioni').delete().eq('note_id', data.id)
      if (condivisiCon.length > 0) {
        await supabase.from('note_condivisioni').insert(condivisiCon.map(uid => ({ note_id: data.id, user_id: uid })))
      }
    }
    onSaved({ ...data, condivisi: condivisiCon })
    onClose()
  }

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return }
    await supabase.from('note').delete().eq('id', nota.id)
    onDeleted(nota.id); onClose()
  }

  const toggleCondiviso = (uid) =>
    setCondivisiCon(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid])

  const inputCls = "w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-violet-500 transition-colors"

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4 py-6">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-900 border-b border-gray-800 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h2 className="text-lg font-bold text-white">{isEdit ? 'Modifica nota' : 'Nuova nota'}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Titolo */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Titolo <span className="text-gray-600">(opzionale)</span></label>
            <input value={titolo} onChange={e => setTitolo(e.target.value)} placeholder="Titolo della nota..." className={inputCls} />
          </div>

          {/* Contenuto */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Contenuto *</label>
            <textarea value={contenuto} onChange={e => setContenuto(e.target.value)} rows={6} required autoFocus
              placeholder="Scrivi qui la nota..." className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-violet-500 transition-colors resize-none" />
          </div>

          {/* Visibilità */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Visibilità</label>
            <div className="grid grid-cols-2 gap-2">
              {VISIBILITA.map(v => {
                const Icon = v.icon
                const sel = visibilita === v.id
                return (
                  <button key={v.id} type="button" onClick={() => setVisibilita(v.id)}
                    className={`flex items-start gap-3 p-3 rounded-xl border text-left transition-colors ${sel ? `${v.bg} ${v.border} border` : 'border-gray-800 hover:border-gray-700'}`}>
                    <Icon size={16} className={sel ? v.text : 'text-gray-600'} />
                    <div>
                      <p className={`text-sm font-medium ${sel ? v.text : 'text-gray-400'}`}>{v.label}</p>
                      <p className="text-xs text-gray-600 mt-0.5">{v.desc}</p>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Progetto selector */}
          {visibilita === 'progetto' && (
            <div>
              <label className="block text-sm text-gray-400 mb-1">Progetto</label>
              <select value={projectId} onChange={e => setProjectId(e.target.value)} className={inputCls}>
                <option value="">— Seleziona progetto —</option>
                {progetti.map(p => <option key={p.id} value={p.id}>{p.titolo}</option>)}
              </select>
            </div>
          )}

          {/* Persone selezionate */}
          {visibilita === 'condivisa' && (
            <div>
              <label className="block text-sm text-gray-400 mb-2">Condividi con</label>
              <div className="flex flex-wrap gap-2">
                {profiles.filter(p => p.id !== profile.id).map(p => {
                  const sel = condivisiCon.includes(p.id)
                  const nome = p.nickname || p.full_name || 'Utente'
                  return (
                    <button key={p.id} type="button" onClick={() => toggleCondiviso(p.id)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border transition-colors ${sel ? 'bg-blue-500/20 border-blue-500/40 text-blue-300' : 'border-gray-700 text-gray-400 hover:border-gray-600'}`}>
                      <div className="w-5 h-5 rounded-full bg-violet-600 flex items-center justify-center text-white text-xs font-bold overflow-hidden flex-shrink-0">
                        {p.avatar_url ? <img src={p.avatar_url} alt="" className="w-full h-full object-cover" /> : nome[0].toUpperCase()}
                      </div>
                      {nome}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {error && <p className="text-red-400 text-sm">{error}</p>}

          <div className="flex gap-3 pt-2">
            {isEdit && (
              <button type="button" onClick={handleDelete}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${confirmDelete ? 'bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500 hover:text-white' : 'border border-gray-700 text-gray-500 hover:text-red-400 hover:border-red-500/40'}`}>
                {confirmDelete ? 'Conferma' : 'Elimina'}
              </button>
            )}
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-lg border border-gray-700 text-gray-400 hover:text-white transition-colors text-sm">Annulla</button>
            <button type="submit" disabled={loading || !contenuto.trim()} className="flex-1 py-3 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-medium text-sm transition-colors">
              {loading ? 'Salvataggio...' : isEdit ? 'Salva' : 'Crea nota'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── NoteViewModal ──────────────────────────────────────────────────────────────
const NoteViewModal = ({ nota, currentUserId, onClose, onEdit }) => {
  const vis = visCfg(nota.visibilita)
  const VisIcon = vis.icon
  const isMine = nota.created_by === currentUserId
  const autore = nota.autore
  const nomeAutore = autore?.nickname || autore?.full_name || 'Utente'

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4 py-6" onClick={onClose}>
      <div
        className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-lg max-h-[92vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
            <span className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg border flex-shrink-0 ${vis.bg} ${vis.text} ${vis.border}`}>
              <VisIcon size={11} />
              {vis.label}
            </span>
            {nota.progetto && (
              <span className="flex items-center gap-1.5 text-xs text-gray-500 flex-shrink-0">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: nota.progetto.colore || '#6d28d9' }} />
                {nota.progetto.titolo}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {isMine && (
              <button
                onClick={onEdit}
                title="Modifica"
                className="p-1.5 text-gray-500 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              >
                <Pencil size={15} />
              </button>
            )}
            <button onClick={onClose} className="p-1.5 text-gray-500 hover:text-white hover:bg-gray-800 rounded-lg transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Contenuto scrollabile */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {nota.titolo && (
            <h2 className="text-white font-bold text-xl mb-4 leading-snug">{nota.titolo}</h2>
          )}
          <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">{nota.contenuto}</p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-3 text-xs text-gray-600">
            {!isMine && <span>{nomeAutore}</span>}
            <span>{new Date(nota.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
          </div>
          {isMine && (
            <button
              onClick={onEdit}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-300 hover:text-white transition-colors"
            >
              <Pencil size={13} />
              Modifica
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── NoteCard ───────────────────────────────────────────────────────────────────
const NoteCard = ({ nota, currentUserId, onView, onEdit }) => {
  const vis = visCfg(nota.visibilita)
  const VisIcon = vis.icon
  const isMine = nota.created_by === currentUserId
  const autore = nota.autore
  const nomeAutore = autore?.nickname || autore?.full_name || 'Utente'

  return (
    <div
      onClick={() => onView(nota)}
      className="bg-gray-900 border border-gray-800 rounded-2xl p-5 flex flex-col gap-3 transition-colors hover:border-gray-700 cursor-pointer"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {nota.titolo && <p className="text-white font-semibold text-sm truncate mb-1">{nota.titolo}</p>}
          <p className="text-gray-400 text-sm line-clamp-4 leading-relaxed whitespace-pre-wrap">{nota.contenuto}</p>
        </div>
        {isMine && (
          <button onClick={e => { e.stopPropagation(); onEdit(nota) }} className="text-gray-600 hover:text-gray-400 transition-colors flex-shrink-0 mt-0.5">
            <Pencil size={14} />
          </button>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-800/80">
        <div className="flex items-center gap-2">
          {/* Visibilità badge */}
          <span className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg border ${vis.bg} ${vis.text} ${vis.border}`}>
            <VisIcon size={11} />
            {vis.label}
          </span>
          {/* Progetto */}
          {nota.progetto && (
            <span className="flex items-center gap-1.5 text-xs text-gray-500">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: nota.progetto.colore || '#6d28d9' }} />
              {nota.progetto.titolo}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Autore (se non è mia) */}
          {!isMine && (
            <span className="text-xs text-gray-600">{nomeAutore}</span>
          )}
          <span className="text-xs text-gray-700">
            {new Date(nota.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
          </span>
        </div>
      </div>
    </div>
  )
}

// ─── Note ───────────────────────────────────────────────────────────────────────
const Note = () => {
  const { profile } = useAuth()
  const [note, setNote] = useState([])
  const [progetti, setProgetti] = useState([])
  const [profiles, setProfiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null)
  const [viewModal, setViewModal] = useState(null)
  const [filtro, setFiltro] = useState('tutte')
  const [query, setQuery] = useState('')

  const fetchNote = useCallback(async () => {
    const { data } = await supabase
      .from('note')
      .select('*, progetto:project_id(titolo,colore), autore:created_by(nickname,full_name,avatar_url)')
      .order('updated_at', { ascending: false })
    setNote(data || [])
    setLoading(false)
  }, [])

  useEffect(() => {
    Promise.all([
      supabase.from('progetti').select('id,titolo,colore').order('titolo'),
      supabase.from('profiles').select('id,nickname,full_name,avatar_url').order('nickname'),
    ]).then(([{ data: pr }, { data: pf }]) => {
      setProgetti(pr || [])
      setProfiles(pf || [])
    })
  }, [])

  useEffect(() => {
    fetchNote()
    const channel = supabase
      .channel('note-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'note' }, fetchNote)
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [fetchNote])

  const handleSaved = (saved) => {
    setNote(prev => {
      const exists = prev.find(n => n.id === saved.id)
      return exists ? prev.map(n => n.id === saved.id ? saved : n) : [saved, ...prev]
    })
  }
  const handleDeleted = (id) => setNote(prev => prev.filter(n => n.id !== id))

  const filtered = note.filter(n => {
    const matchFiltro =
      filtro === 'tutte' ? true :
      filtro === 'mie' ? n.created_by === profile?.id :
      n.visibilita === filtro
    const q = query.toLowerCase()
    const matchQuery = !q || n.titolo?.toLowerCase().includes(q) || n.contenuto?.toLowerCase().includes(q)
    return matchFiltro && matchQuery
  })

  const counts = {
    tutte: note.length,
    mie: note.filter(n => n.created_by === profile?.id).length,
    pubblica: note.filter(n => n.visibilita === 'pubblica').length,
    progetto: note.filter(n => n.visibilita === 'progetto').length,
    condivisa: note.filter(n => n.visibilita === 'condivisa').length,
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Note</h1>
          <p className="text-gray-500 text-sm mt-1">{note.length} note</p>
        </div>
        <button
          onClick={() => setModal({})}
          className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white font-medium px-4 py-2.5 rounded-lg transition-colors text-sm"
        >
          <Plus size={15} />
          Nuova nota
        </button>
      </div>

      {/* Filtri + Search */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex gap-1.5 flex-wrap">
          {FILTRI.map(f => (
            <button key={f} onClick={() => setFiltro(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filtro === f ? 'bg-violet-600 text-white' : 'bg-gray-900 border border-gray-800 text-gray-400 hover:border-gray-700'}`}>
              {FILTRI_LABEL[f]}
              {counts[f] > 0 && <span className={`ml-1.5 ${filtro === f ? 'text-violet-200' : 'text-gray-600'}`}>{counts[f]}</span>}
            </button>
          ))}
        </div>
        <div className="relative sm:ml-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Cerca nelle note..."
            className="w-full sm:w-56 bg-gray-900 border border-gray-800 rounded-xl pl-8 pr-4 py-2 text-white placeholder-gray-600 text-sm focus:outline-none focus:border-violet-500 transition-colors" />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-gray-600 text-sm mb-3">{query ? 'Nessun risultato.' : 'Nessuna nota ancora.'}</p>
          {!query && (
            <button onClick={() => setModal({})} className="text-violet-400 hover:text-violet-300 text-sm transition-colors">
              Scrivi la prima nota →
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(n => (
            <NoteCard
              key={n.id}
              nota={n}
              currentUserId={profile?.id}
              onView={setViewModal}
              onEdit={setModal}
            />
          ))}
        </div>
      )}

      {viewModal !== null && (
        <NoteViewModal
          nota={viewModal}
          currentUserId={profile?.id}
          onClose={() => setViewModal(null)}
          onEdit={() => { setModal(viewModal); setViewModal(null) }}
        />
      )}

      {modal !== null && (
        <NoteModal
          nota={modal?.id ? modal : null}
          progetti={progetti}
          profiles={profiles}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  )
}

export default Note
